import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Box, Divider, List, Typography, useMediaQuery } from '@mui/material';

// project-imports
import NavItem from './NavItem';
import NavCollapse from './NavCollapse';
import SIDEBAR from '../../sidebarStyles';

import { dispatch, useSelector } from 'store';
import { activeID } from 'store/reducers/menu';

// ==============================|| NAVIGATION - GROUP ||============================== //

const NavGroup = ({ item, lastItem, remItems, lastItemId, isLastGroup }) => {
  const theme = useTheme();
  const { pathname } = useLocation();

  const { drawerOpen } = useSelector((state) => state.menu);

  const downLG = useMediaQuery(theme.breakpoints.down('lg'));

  const [anchorEl, setAnchorEl] = useState(null);
  const [currentItem, setCurrentItem] = useState(item);

  const openMini = Boolean(anchorEl);

  useEffect(() => {
    if (lastItem) {
      if (item.id === lastItemId) {
        const localItem = { ...item };
        const elements = remItems.map((ele) => ele.elements);
        localItem.children = elements.flat(1);
        setCurrentItem(localItem);
      } else {
        setCurrentItem(item);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, lastItem, downLG]);

  const checkOpenForParent = (child, id) => {
    child && child.length !== 0 && child.forEach((ele) => {
      if (ele && ele?.children && ele.children?.length !== 0) {
        checkOpenForParent(ele.children, currentItem.id);
      }
      if (ele && ele.url === pathname) {
        dispatch(activeID(id));
      }
    });
  };

  const checkSelectedOnload = (data) => {
    const childrens = data.children ? data.children : [];
    childrens && childrens.length !== 0 && childrens.forEach((itemCheck) => {
      if (itemCheck && itemCheck?.children && itemCheck.children?.length) {
        checkOpenForParent(itemCheck && itemCheck.children, currentItem.id);
      }
      if (itemCheck && itemCheck.url === pathname) {
        dispatch(activeID(currentItem.id));
      }
    });
  };

  useEffect(() => {
    checkSelectedOnload(currentItem);
    if (openMini) setAnchorEl(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, currentItem]);

  const navCollapse = item.children?.map((menuItem) => {
    switch (menuItem && menuItem.type) {
      case 'collapse':
        return <NavCollapse key={menuItem && menuItem.id} item={menuItem} level={1} />;
      case 'item':
        return <NavItem key={menuItem && menuItem.id} item={menuItem} level={1} />;
      default:
        return (
          <Typography key={menuItem && menuItem.id} variant="caption" color="error" align="center" sx={{ display: 'block', px: 2 }}>
            Fix - Group Collapse or Items
          </Typography>
        );
    }
  });

  return (
    <>
      <List
        subheader={
          item.title &&
          drawerOpen && (
            <Box sx={{ px: 2.75, pt: 2.25, pb: 1 }}>
              <Typography
                variant="overline"
                sx={{
                  color: SIDEBAR.sectionLabel,
                  fontSize: '0.6875rem',
                  fontWeight: 700,
                  letterSpacing: '0.09em',
                  textTransform: 'uppercase',
                  lineHeight: 1.2,
                  display: 'block'
                }}
              >
                {item.title}
              </Typography>
              {item.caption && (
                <Typography variant="caption" sx={{ color: SIDEBAR.sectionLabel, fontSize: '0.65rem', mt: 0.25, display: 'block' }}>
                  {item.caption}
                </Typography>
              )}
            </Box>
          )
        }
        sx={{ mt: 0, py: item.title ? 0.5 : 0, zIndex: 0 }}
      >
        {navCollapse}
      </List>

      {/* Thin divider between groups — only shown when this group has a visible title and is not the last group */}
      {drawerOpen && !isLastGroup && item.title && (
        <Divider
          sx={{
            mx: 2.25,
            my: 0.75,
            borderColor: SIDEBAR.divider
          }}
        />
      )}
    </>
  );
};

NavGroup.propTypes = {
  item: PropTypes.object,
  lastItem: PropTypes.bool,
  remItems: PropTypes.array,
  lastItemId: PropTypes.string,
  isLastGroup: PropTypes.bool,
  children: PropTypes.node,
  title: PropTypes.string,
  caption: PropTypes.string
};

export default NavGroup;
